import * as THREE from 'three';
// F-GP-011 (audits/2026-05-07): dropped unused PLAYER_HEIGHT /
// PLAYER_CROUCH_HEIGHT / PLAYER_RADIUS imports — none of them appear
// outside the import block. Reader-hostile dead refs.
import {
  WeaponConfig,
  WeaponId,
  WEAPONS,
  MOVEMENT_INACCURACY_MULT,
  CROUCH_ACCURACY_MULT,
  RECOIL_RESET_MS,
  FIRST_SHOT_ACCURACY,
  HEAD_MULT,
  CHEST_MULT,
  STOMACH_MULT,
  LEGS_MULT,
  ARMOR_ABSORPTION,
} from '../core/config';
import { randomInCone, clamp } from '../utils/math';
import { playWeaponSwitch, playBoltAction } from '../audio/sounds';
import {
  getSprayOffset as _getSprayOffset,
  SPRAY_RECOVERY_RATE,
  SPRAY_CROUCH_RECOVERY_MULT,
  type SprayPoint,
} from './spray-patterns';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WeaponState {
  id: WeaponId;
  ammo: number;
  reserveAmmo: number;
  isReloading: boolean;
  reloadStartTime: number;
  lastFireTime: number;
  lastSpreadUpdateTime?: number;
  currentSpread: number;
  shotsFired: number; // for recoil buildup
}

export type BodyPart = 'head' | 'chest' | 'stomach' | 'legs';

export interface HitResult {
  hit: boolean;
  point: THREE.Vector3;
  normal?: THREE.Vector3;
  distance: number;
  hitPlayer?: {
    id: number;
    bodyPart: BodyPart;
  };
  /** The Three.js object that was hit (for surface type detection). */
  object?: THREE.Object3D;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BODY_PART_MULTS: Record<BodyPart, number> = {
  head: HEAD_MULT,
  chest: CHEST_MULT,
  stomach: STOMACH_MULT,
  legs: LEGS_MULT,
};

/** How quickly spread decays back to base (degrees per second). */
const SPREAD_DECAY_RATE = 8.0;

// ---------------------------------------------------------------------------
// Recoil patterns — per-weapon camera kick on each shot
// ---------------------------------------------------------------------------

export interface RecoilKick {
  /** Vertical camera kick in radians (positive = upward) */
  pitchKick: number;
  /** Horizontal camera kick in radians (random spread) */
  yawKick: number;
  /** Viewmodel position kick backward (z) */
  posZ: number;
  /** Viewmodel position kick upward (y) */
  posY: number;
  /** Viewmodel rotation kick (x axis, radians) */
  rotX: number;
  /** Recovery speed: lerp factor per frame toward rest */
  recovery: number;
}

const RECOIL_PATTERNS: Record<WeaponId, RecoilKick> = {
  glock: {
    pitchKick: 0.018, yawKick: 0.008,
    posZ: 0.03, posY: 0.02, rotX: 0.06,
    recovery: 0.15,
  },
  usp: {
    pitchKick: 0.015, yawKick: 0.006,
    posZ: 0.025, posY: 0.015, rotX: 0.05,
    recovery: 0.15,
  },
  ak47: {
    pitchKick: 0.025, yawKick: 0.015,
    posZ: 0.02, posY: 0.015, rotX: 0.04,
    recovery: 0.10,
  },
  m4a4: {
    pitchKick: 0.02, yawKick: 0.012,
    posZ: 0.018, posY: 0.012, rotX: 0.035,
    recovery: 0.10,
  },
  awp: {
    pitchKick: 0.06, yawKick: 0.01,
    posZ: 0.08, posY: 0.04, rotX: 0.12,
    recovery: 0.06,
  },
  p250: {
    pitchKick: 0.02, yawKick: 0.009,
    posZ: 0.035, posY: 0.025, rotX: 0.065,
    recovery: 0.15,
  },
  deagle: {
    pitchKick: 0.045, yawKick: 0.012,
    posZ: 0.06, posY: 0.04, rotX: 0.10,
    recovery: 0.08,
  },
  mp9: {
    pitchKick: 0.012, yawKick: 0.008,
    posZ: 0.015, posY: 0.01, rotX: 0.03,
    recovery: 0.18,
  },
  mac10: {
    pitchKick: 0.014, yawKick: 0.01,
    posZ: 0.018, posY: 0.012, rotX: 0.035,
    recovery: 0.16,
  },
  famas: {
    pitchKick: 0.018, yawKick: 0.011,
    posZ: 0.016, posY: 0.012, rotX: 0.032,
    recovery: 0.11,
  },
  galil: {
    pitchKick: 0.02, yawKick: 0.013,
    posZ: 0.018, posY: 0.013, rotX: 0.036,
    recovery: 0.10,
  },
  nova: {
    pitchKick: 0.055, yawKick: 0.015,
    posZ: 0.07, posY: 0.04, rotX: 0.11,
    recovery: 0.07,
  },
  ssg08: {
    pitchKick: 0.04, yawKick: 0.008,
    posZ: 0.05, posY: 0.03, rotX: 0.08,
    recovery: 0.08,
  },
  aug: {
    pitchKick: 0.019, yawKick: 0.010,
    posZ: 0.017, posY: 0.012, rotX: 0.034,
    recovery: 0.11,
  },
  sg553: {
    pitchKick: 0.022, yawKick: 0.013,
    posZ: 0.019, posY: 0.014, rotX: 0.038,
    recovery: 0.10,
  },
  p90: {
    pitchKick: 0.013, yawKick: 0.009,
    posZ: 0.016, posY: 0.011, rotX: 0.032,
    recovery: 0.17,
  },
  fiveseven: {
    pitchKick: 0.019, yawKick: 0.008,
    posZ: 0.032, posY: 0.022, rotX: 0.062,
    recovery: 0.15,
  },
  tec9: {
    pitchKick: 0.020, yawKick: 0.010,
    posZ: 0.034, posY: 0.024, rotX: 0.065,
    recovery: 0.14,
  },
  m4a1s: {
    pitchKick: 0.018, yawKick: 0.010,
    posZ: 0.016, posY: 0.011, rotX: 0.032,
    recovery: 0.11,
  },
  knife: {
    pitchKick: 0.005, yawKick: 0.02,
    posZ: -0.06, posY: 0.01, rotX: 0.15,
    recovery: 0.20,
  },
};

/**
 * Get the recoil kick parameters for a weapon.
 * The yawKick is randomized left/right each call.
 */
export function getRecoilKick(weaponId: WeaponId, shotsFired: number): RecoilKick {
  const base = RECOIL_PATTERNS[weaponId];
  // Increase recoil slightly with sustained fire (rifles and SMGs)
  const cfg = WEAPONS[weaponId];
  const isAutomatic = cfg.type === 'rifle' || cfg.type === 'smg';
  const burstMult = isAutomatic
    ? Math.min(1 + shotsFired * 0.08, 2.0)
    : 1.0;
  return {
    ...base,
    pitchKick: base.pitchKick * burstMult,
    yawKick: base.yawKick * (Math.random() > 0.5 ? 1 : -1) * burstMult,
  };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createWeaponState(id: WeaponId): WeaponState {
  const cfg = WEAPONS[id];
  return {
    id,
    ammo: cfg.magazine,
    reserveAmmo: cfg.reserveAmmo,
    isReloading: false,
    reloadStartTime: 0,
    lastFireTime: 0,
    lastSpreadUpdateTime: 0,
    currentSpread: cfg.baseSpread,
    shotsFired: 0,
  };
}

/**
 * Create a new weapon state and play the weapon switch sound.
 * Use this instead of bare `createWeaponState` when the player actively
 * switches weapons so that audible feedback is provided.
 */
export function switchToWeapon(id: WeaponId): WeaponState {
  playWeaponSwitch();
  return createWeaponState(id);
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function getConfig(state: WeaponState): WeaponConfig {
  return WEAPONS[state.id];
}

/**
 * Can we fire right now? Checks fire-rate cooldown, ammo, and reload status.
 */
export function canFire(state: WeaponState, now: number): boolean {
  if (state.isReloading) return false;

  const cfg = getConfig(state);
  // Melee weapons have infinite ammo
  if (cfg.type !== 'melee' && state.ammo <= 0) return false;

  const fireIntervalMs = 60_000 / cfg.fireRateRPM;
  return now - state.lastFireTime >= fireIntervalMs;
}

// ---------------------------------------------------------------------------
// State transitions (all pure – return new state)
// ---------------------------------------------------------------------------

/**
 * Fire the weapon. Caller must check `canFire` first.
 * Returns updated state with decremented ammo, updated spread, etc.
 */
export function fire(state: WeaponState, now: number): WeaponState {
  const cfg = getConfig(state);

  // Guard: don't fire with no ammo (melee has infinite ammo)
  if (cfg.type !== 'melee' && state.ammo <= 0) return state;

  const newSpread = clamp(
    state.currentSpread + cfg.spreadPerShot,
    cfg.baseSpread,
    cfg.maxSpread,
  );

  return {
    ...state,
    // Melee weapons don't consume ammo
    ammo: cfg.type === 'melee' ? state.ammo : state.ammo - 1,
    lastFireTime: now,
    lastSpreadUpdateTime: now,
    currentSpread: newSpread,
    shotsFired: state.shotsFired + 1,
  };
}

/**
 * Begin a reload. No-ops (returns same state) if already reloading or full.
 */
export function startReload(state: WeaponState, now: number): WeaponState {
  if (state.isReloading) return state;

  const cfg = getConfig(state);
  if (state.ammo >= cfg.magazine) return state;
  if (state.reserveAmmo <= 0) return state;

  return {
    ...state,
    isReloading: true,
    reloadStartTime: now,
  };
}

/**
 * Call every frame while reloading. When the timer expires, moves ammo from
 * reserve into the magazine and clears the reloading flag.
 */
export function updateReload(state: WeaponState, now: number): WeaponState {
  if (!state.isReloading) return state;

  const cfg = getConfig(state);
  const elapsed = now - state.reloadStartTime;
  if (elapsed < cfg.reloadTimeMs) return state;

  // Reload complete — fill magazine from reserve
  const needed = cfg.magazine - state.ammo;
  const transferred = Math.min(needed, state.reserveAmmo);

  // Play bolt action cycling sound for bolt-action snipers
  if (state.id === 'awp' || state.id === 'ssg08') {
    playBoltAction();
  }

  return {
    ...state,
    ammo: state.ammo + transferred,
    reserveAmmo: state.reserveAmmo - transferred,
    isReloading: false,
    reloadStartTime: 0,
    shotsFired: 0,
  };
}

// ---------------------------------------------------------------------------
// Spread helpers
// ---------------------------------------------------------------------------

/**
 * Total spread right now, taking movement, crouching, and recoil decay into
 * account.
 */
export function getCurrentSpread(
  state: WeaponState,
  isMoving: boolean,
  isCrouching: boolean,
  now: number,
): number {
  const cfg = getConfig(state);

  // First-shot accuracy: if we haven't fired recently, spread is perfect
  const timeSinceShot = now - state.lastFireTime;
  if (state.shotsFired === 0 || timeSinceShot >= RECOIL_RESET_MS) {
    let base = FIRST_SHOT_ACCURACY + cfg.baseSpread;
    if (isMoving) base *= MOVEMENT_INACCURACY_MULT;
    if (isCrouching) base *= CROUCH_ACCURACY_MULT;
    return base;
  }

  let spread = state.currentSpread;

  // Decay toward base since last shot (crouching accelerates recovery)
  const crouchDecayMult = isCrouching ? 1.5 : 1.0;
  const decayAmount = SPREAD_DECAY_RATE * crouchDecayMult * (timeSinceShot / 1000);
  spread = Math.max(cfg.baseSpread, spread - decayAmount);

  if (isMoving) spread *= MOVEMENT_INACCURACY_MULT;
  if (isCrouching) spread *= CROUCH_ACCURACY_MULT;

  return clamp(spread, 0, cfg.maxSpread);
}

/**
 * Passively decay the stored spread toward baseSpread when not firing.
 * Call once per frame.
 */
export function updateSpread(state: WeaponState, now: number, isCrouching = false): WeaponState {
  const cfg = getConfig(state);
  const timeSinceShot = now - state.lastFireTime;

  if (timeSinceShot >= RECOIL_RESET_MS) {
    // Full reset
    return {
      ...state,
      lastSpreadUpdateTime: now,
      currentSpread: cfg.baseSpread,
      shotsFired: 0,
    };
  }

  // Crouching accelerates spread recovery (matches CS:GO behavior)
  const crouchMult = isCrouching ? 1.5 : 1.0;
  const lastSpreadUpdateTime = Math.max(state.lastSpreadUpdateTime ?? state.lastFireTime, state.lastFireTime);
  const decayDt = Math.max(0, now - lastSpreadUpdateTime);
  const decayAmount = SPREAD_DECAY_RATE * crouchMult * (decayDt / 1000);
  const newSpread = Math.max(cfg.baseSpread, state.currentSpread - decayAmount);

  return {
    ...state,
    lastSpreadUpdateTime: now,
    currentSpread: newSpread,
  };
}

// ---------------------------------------------------------------------------
// Hitscan
// ---------------------------------------------------------------------------

const _raycaster = new THREE.Raycaster();
const _spreadDir = new THREE.Vector3();

/**
 * Perform a hitscan ray against the scene.
 *
 * Player hitboxes are expected to be THREE.Mesh objects whose `userData`
 * contains `{ playerId: number }`. The mesh name encodes the body part:
 * `"hitbox_head"`, `"hitbox_chest"`, `"hitbox_stomach"`, `"hitbox_legs"`.
 *
 * Non-player geometry (walls, floors, etc.) is also tested — the closest
 * intersection wins.
 */
export function performHitscan(
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  spread: number,
  scene: THREE.Scene,
): HitResult {
  // Apply spread
  _spreadDir.copy(direction);
  const spreadDir = spread > 0 ? randomInCone(_spreadDir, spread) : _spreadDir.clone().normalize();

  _raycaster.set(origin, spreadDir);
  _raycaster.far = 8192; // max trace distance (units)
  _raycaster.near = 0;
  // Raycaster tests layer 0 only (default). Effect objects are on layer 1,
  // so Three.js skips them entirely — no intersection math wasted.
  _raycaster.layers.set(0);

  const intersections = _raycaster.intersectObjects(scene.children, true);

  // Skip non-world geometry (invisible, batched-away, or effect objects)
  let closest: THREE.Intersection | undefined;
  for (const hit of intersections) {
    const ud = hit.object.userData;
    if (hit.object.visible && !ud.batchedAway && !ud.isEffect && !ud.isLocalPlayer) {
      closest = hit;
      break;
    }
  }

  if (!closest) {
    return {
      hit: false,
      point: origin.clone().addScaledVector(spreadDir, 8192),
      distance: 8192,
    };
  }
  // Get world-space normal from the hit face
  let normal = new THREE.Vector3(0, 1, 0); // fallback
  if (closest.face) {
    normal = closest.face.normal.clone();
    normal.transformDirection(closest.object.matrixWorld);
  }

  const result: HitResult = {
    hit: true,
    point: closest.point.clone(),
    normal,
    distance: closest.distance,
    object: closest.object,
  };

  // Check if we hit a player hitbox.
  // Validate that the playerId is a finite number to guard against stale
  // userData on objects that have been recycled or improperly tagged.
  const obj = closest.object;
  const playerId = obj.userData?.playerId as number | undefined;
  if (playerId !== undefined && Number.isFinite(playerId)) {
    const bodyPart = parseBodyPart(obj.name);
    result.hitPlayer = {
      id: playerId,
      bodyPart,
    };
  }

  return result;
}

function parseBodyPart(name: string): BodyPart {
  if (name.includes('head')) return 'head';
  if (name.includes('stomach')) return 'stomach';
  if (name.includes('legs')) return 'legs';
  return 'chest'; // default
}

// ---------------------------------------------------------------------------
// Damage calculation
// ---------------------------------------------------------------------------

/**
 * Calculate final damage for a hit.
 *
 * Formula: baseDamage * bodyPartMult * (hasArmor ? armorReduction : 1)
 * Armor reduces damage by `ARMOR_ABSORPTION` fraction, scaled by the
 * weapon's penetration value (higher penetration → more damage through armor).
 */
export function calculateDamage(
  weaponId: WeaponId,
  bodyPart: BodyPart,
  hasArmor: boolean,
): number {
  const cfg = WEAPONS[weaponId];
  const partMult = BODY_PART_MULTS[bodyPart];
  let damage = cfg.damage * partMult;

  if (hasArmor) {
    // Penetration of 1.0 means armor does nothing; 0.0 means full absorption
    const armorReduction = ARMOR_ABSORPTION * (1 - cfg.penetration);
    damage *= 1 - armorReduction;
  }

  return Math.round(damage);
}

// ---------------------------------------------------------------------------
// Spray pattern re-exports — callers can access spray data via weapon module
// ---------------------------------------------------------------------------

/**
 * Get the deterministic spray offset for a given weapon and shot index.
 * Returns `{ pitch: 0, yaw: 0 }` for weapons without a pattern (knife).
 *
 * @param weaponId - The weapon being fired
 * @param shotIndex - Zero-based shot index (0 = first shot)
 * @returns Pitch and yaw offset in degrees
 */
export function getSprayOffset(weaponId: WeaponId, shotIndex: number): SprayPoint {
  return _getSprayOffset(weaponId, shotIndex, 0);
}

export { SPRAY_RECOVERY_RATE, SPRAY_CROUCH_RECOVERY_MULT };
